import React, { useCallback, useEffect, useState } from 'react';
import {
  useSelector,
} from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Grid, Typography,
} from '@mui/material';
import {
  DigitalQuestion, DigitalSection, RootState,
} from '../../redux/types';
import {
  fetchTrial,
  navigateBack,
} from '../../redux/actions';
import SectionCopySelector from '../../components/survey/copy/SectionCopySelector';
import SectionCopyDetails from '../../components/survey/copy/SectionCopyDetails';
import Prompt from '../../components/layout/Prompt';
import ApiClient from '../../loopback/apiClient';
import { CopySectionRequestType, CopyQuestionRequestType } from '../../loopback/apiClient/DRTApi';
import QuestionCopyDetails from '../../components/survey/copy/QuestionCopyDetails';
import { useAppDispatch } from '../../hooks/reduxHooks';

type MatchParams = {
  trialId: string;
  surveyId: string;
  sectionId?: string;
};

type DigitalQuestionExtended = DigitalQuestion & {
  selected?: boolean;
};

type Props = {
  isQuestionCopier?: boolean;
}

const SurveyCopyView: React.FC<Props> = ({
  isQuestionCopier,
}: Props) => {
  const [searchParams] = useSearchParams();
  const match = useParams<MatchParams>();
  const { trialId, surveyId, sectionId } = match;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isContextQuestion = searchParams.get('context') === 'true';
  const [
    selectedSections, setSelectedSections,
  ] = useState<Map<number, DigitalSection>>(new Map());
  const [
    selectedSurveyQuestions, setSelectedSurveyQuestions,
  ] = useState<Map<number, DigitalQuestionExtended>>(new Map());
  const [confirmCopy, setConfirmCopy] = useState<boolean>(false);
  const trial = useSelector((state: RootState) => state.trial.trial);

  useEffect(() => {
    dispatch(fetchTrial(Number(trialId)));
  }, [dispatch, trialId]);

  const handleSectionSelect = useCallback((
    event: React.ChangeEvent<HTMLInputElement>,
    section: DigitalSection,
    questions: DigitalQuestion[],
  ) => {
    const { checked } = event.target;
    if (checked) {
      setSelectedSections(new Map([...selectedSections, [section.id, section]]));
      let filteredQuestions = questions
        .filter((qu) => qu.digitalSectionId === section.id);

      // filter context question or main questions
      if (isContextQuestion) {
        filteredQuestions = filteredQuestions.filter((q) => q.isContextQuestion);
      } else if (isQuestionCopier) {
        filteredQuestions = filteredQuestions.filter((q) => !q.isContextQuestion);
      }

      // final step: map to array of [id, question] tuples
      const mappedQuestions = filteredQuestions.map((q) => [q.id, q] as [number, DigitalQuestion]);
      setSelectedSurveyQuestions(new Map([
        ...selectedSurveyQuestions,
        ...mappedQuestions,
      ]));
    } else {
      selectedSections.delete(section.id);
      setSelectedSections(new Map(selectedSections));
      questions.forEach((q) => {
        if (q.digitalSectionId && !selectedSections.get(q.digitalSectionId)) {
          selectedSurveyQuestions.delete(q.id);
        }
      });
      setSelectedSurveyQuestions(new Map(selectedSurveyQuestions));
    }
  }, [
    selectedSections, selectedSurveyQuestions, isContextQuestion, isQuestionCopier,
  ]);

  const handleQuestionSelect = useCallback((qId: number, checked: boolean) => {
    const quest = selectedSurveyQuestions.get(qId);
    if (!quest) {
      return;
    }
    const selectedQuest: DigitalQuestionExtended = {
      ...quest,
      selected: checked,
    };
    setSelectedSurveyQuestions(new Map([
      ...selectedSurveyQuestions,
      [qId, selectedQuest],
    ]));
  }, [selectedSurveyQuestions]);

  const handleCopyClick = () => {
    setConfirmCopy(true);
  };

  const execCopySections = () => {
    const payload: CopySectionRequestType = {
      trialId: Number(trialId),
      surveyId: Number(surveyId),
      copySectionIds: [...selectedSections.values()].map((s) => s.id),
    };
    ApiClient.copyDigitalSectionsToTrial(payload).then(() => {
      navigate(-1);
    });
  };

  const execCopyQuestions = () => {
    if (!trialId || !surveyId || !sectionId) {
      return;
    }
    const selectedQuestions = [...selectedSurveyQuestions]
      .filter(([, q]) => q.selected)
      .map(([, v]) => v);
    const payload: CopyQuestionRequestType = {
      trialId: Number(trialId),
      surveyId: Number(surveyId),
      sectionId: Number(sectionId),
      copyQuestionIds: selectedQuestions.map((q) => q.id),
    };
    ApiClient.copyDigitalQuestionsToTrial(payload).then(() => navigate(-1));
  };

  const handleConfirmCopy = (confirm: boolean) => {
    setConfirmCopy(false);
    if (!confirm) {
      return;
    }

    if (isQuestionCopier) {
      execCopyQuestions();
    } else {
      execCopySections();
    }
  };

  let title;
  if (isContextQuestion) {
    title = 'Context Questions';
  } else if (isQuestionCopier) {
    title = 'Questions';
  } else {
    title = 'Sections';
  }

  return (
    <Box sx={{ m: 1 }}>
      <Grid container sx={{ minHeight: '98vh' }}>
        <Grid item xs={12} sm={12} md={8}>
          {
            isQuestionCopier && surveyId && sectionId ? (
              <QuestionCopyDetails
                title={title}
                sections={selectedSections}
                questions={selectedSurveyQuestions}
                trial={trial}
                onSelect={handleQuestionSelect}
              />
            ) : (
              <SectionCopyDetails
                sections={selectedSections}
                questions={selectedSurveyQuestions}
                trial={trial}
              />
            )
          }
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          sx={{
            top: 0, maxHeight: '98vh', position: 'sticky', overflow: 'hidden', borderLeft: '1px solid #f0f0f0',
          }}
        >
          <Box display="flex" flexDirection="column">
            <Box height="93vh" overflow="auto">
              <SectionCopySelector
                currentTrialId={Number(trialId)}
                selectedSections={selectedSections}
                onSectionSelect={handleSectionSelect}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" sx={{ p: 1 }}>
              <Button
                variant="contained"
                color="error"
                sx={{ mx: 0.5 }}
                onClick={() => navigateBack(navigate)}
              >
                Cancel
              </Button>
              <Button variant="contained" color="primary" sx={{ mx: 0.5 }} onClick={handleCopyClick}>Copy</Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Prompt
        display={confirmCopy}
        title={`Copy ${title} to Trial`}
        action={handleConfirmCopy}
        version={2}
      >
        <Typography>
          {`Are you sure you want to copy the following ${title} to the trial ${trial?.name}`}
        </Typography>
        <ul>
          {
            isQuestionCopier ? (
              [...selectedSurveyQuestions]
                .filter(([, q]) => q.selected)
                .map(([, q]) => (
                  <li key={q.id}><Typography>{q.text}</Typography></li>
                ))
            ) : (
              [...selectedSections].map(([, sec]) => (
                <li key={sec.id}><Typography>{sec.name}</Typography></li>
              ))
            )
          }
        </ul>
      </Prompt>
    </Box>
  );
};

export default SurveyCopyView;
